import { useState, useEffect, useCallback } from 'react'
import {
  Shield,
  Users,
  Flag,
  Loader2,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Plus,
  Trash2,
  RefreshCw,
  UserCheck,
} from 'lucide-react'
import { adminService } from '../../services/adminService'

// ─── Types ─────────────────────────────────────────────────────────────────────

interface PermissionOverride {
  id: string
  userId: string
  feature: string
  granted: boolean
  setBy: string
  createdAt: string
}

interface PermissionMatrix {
  roles: string[]
  features: string[]
  permissions: Record<string, Record<string, boolean>>
  overrides: PermissionOverride[]
}

interface FeatureFlag {
  id: string
  name: string
  description: string | null
  enabled: boolean
  targetRoles: string[]
  conditions: Record<string, unknown>
  createdAt: string
  updatedAt: string
}

type TabId = 'matrix' | 'overrides' | 'flags'

interface TabItem {
  id: TabId
  label: string
  icon: React.ElementType
}

// ─── Constants ─────────────────────────────────────────────────────────────────

const TABS: TabItem[] = [
  { id: 'matrix', label: 'Permission Matrix', icon: Shield },
  { id: 'overrides', label: 'User Overrides', icon: Users },
  { id: 'flags', label: 'Feature Flags', icon: Flag },
]

// ─── Component ─────────────────────────────────────────────────────────────────

export function AdminAccessControl() {
  const [activeTab, setActiveTab] = useState<TabId>('matrix')

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
          Access Control
        </h1>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
          Manage role-based permissions, user-level overrides, and feature flags.
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="-mb-px flex space-x-4 overflow-x-auto" aria-label="Access control tabs">
          {TABS.map((tab) => {
            const Icon = tab.icon
            const isActive = activeTab === tab.id
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 whitespace-nowrap border-b-2 px-3 py-3 text-sm font-medium transition-colors ${
                  isActive
                    ? 'border-blue-600 text-blue-600 dark:border-blue-400 dark:text-blue-400'
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
                aria-current={isActive ? 'page' : undefined}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
              </button>
            )
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div>
        {activeTab === 'matrix' && <PermissionMatrixTab />}
        {activeTab === 'overrides' && <UserOverridesTab />}
        {activeTab === 'flags' && <FeatureFlagsTab />}
      </div>
    </div>
  )
}


// ─── Permission Matrix Tab ─────────────────────────────────────────────────────

function PermissionMatrixTab() {
  const [matrix, setMatrix] = useState<PermissionMatrix | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [togglingCell, setTogglingCell] = useState<string | null>(null)

  const fetchMatrix = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const res = await adminService.getPermissionMatrix()
      const data = res.data?.matrix || res.data || null
      setMatrix(data)
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load permission matrix')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetchMatrix() }, [fetchMatrix])

  const handleToggle = async (role: string, feature: string, currentlyGranted: boolean) => {
    const cellKey = `${role}:${feature}`
    try {
      setTogglingCell(cellKey)
      if (currentlyGranted) {
        await adminService.denyPermission(role, feature)
      } else {
        await adminService.grantPermission(role, feature)
      }
      // Optimistically update local state
      setMatrix((prev) => {
        if (!prev) return prev
        const updated = { ...prev, permissions: { ...prev.permissions } }
        updated.permissions[role] = { ...updated.permissions[role], [feature]: !currentlyGranted }
        return updated
      })
    } catch (err: unknown) {
      const message = (err as any)?.message || (err instanceof Error ? err.message : 'Failed to update permission')
      alert(message)
    } finally {
      setTogglingCell(null)
    }
  }

  // Check if a feature has any user-level overrides
  const getOverridesForFeature = (feature: string): PermissionOverride[] => {
    if (!matrix?.overrides) return []
    return matrix.overrides.filter((o) => o.feature === feature)
  }

  if (loading) return <LoadingState />
  if (error) return <ErrorState message={error} onRetry={fetchMatrix} />
  if (!matrix || !matrix.roles || !matrix.features) {
    return <EmptyState message="No permission data available." />
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Toggle permissions for each role. Roles are columns, features are rows.
        </p>
        <button
          onClick={fetchMatrix}
          className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
        >
          <RefreshCw className="h-3 w-3" />
          Refresh
        </button>
      </div>

      <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                <th className="text-left px-4 py-3 font-medium text-gray-700 dark:text-gray-300 sticky left-0 bg-gray-50 dark:bg-gray-800/50 z-10 min-w-[180px]">
                  Feature
                </th>
                {matrix.roles.map((role) => (
                  <th
                    key={role}
                    className="text-center px-3 py-3 font-medium text-gray-700 dark:text-gray-300 min-w-[100px]"
                  >
                    <span className="capitalize text-xs">{role.replace(/_/g, ' ')}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {matrix.features.map((feature) => {
                const featureOverrides = getOverridesForFeature(feature)
                const hasOverrides = featureOverrides.length > 0

                return (
                  <tr key={feature} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                    <td className="px-4 py-3 sticky left-0 bg-white dark:bg-gray-800 z-10">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900 dark:text-white text-xs">
                          {feature.replace(/_/g, ' ').replace(/\./g, ' › ')}
                        </span>
                        {hasOverrides && (
                          <span
                            className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300"
                            title={`${featureOverrides.length} user override(s)`}
                          >
                            <UserCheck className="h-2.5 w-2.5" />
                            {featureOverrides.length}
                          </span>
                        )}
                      </div>
                    </td>
                    {matrix.roles.map((role) => {
                      const granted = matrix.permissions[role]?.[feature] ?? false
                      const cellKey = `${role}:${feature}`
                      const isToggling = togglingCell === cellKey

                      return (
                        <td key={cellKey} className="text-center px-3 py-3">
                          <button
                            onClick={() => handleToggle(role, feature, granted)}
                            disabled={isToggling}
                            className={`inline-flex items-center justify-center h-7 w-7 rounded-md transition-all duration-150 disabled:opacity-50 ${
                              granted
                                ? 'bg-green-100 text-green-700 hover:bg-green-200 dark:bg-green-900/30 dark:text-green-400 dark:hover:bg-green-900/50'
                                : 'bg-red-100 text-red-600 hover:bg-red-200 dark:bg-red-900/30 dark:text-red-400 dark:hover:bg-red-900/50'
                            }`}
                            title={`${granted ? 'Deny' : 'Grant'} ${feature} for ${role}`}
                            aria-label={`${granted ? 'Deny' : 'Grant'} ${feature} for ${role}`}
                          >
                            {isToggling ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : granted ? (
                              <CheckCircle className="h-3.5 w-3.5" />
                            ) : (
                              <XCircle className="h-3.5 w-3.5" />
                            )}
                          </button>
                        </td>
                      )
                    })}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
        <div className="flex items-center gap-1.5">
          <span className="inline-flex h-5 w-5 items-center justify-center rounded bg-green-100 dark:bg-green-900/30">
            <CheckCircle className="h-3 w-3 text-green-700 dark:text-green-400" />
          </span>
          Granted
        </div>
        <div className="flex items-center gap-1.5">
          <span className="inline-flex h-5 w-5 items-center justify-center rounded bg-red-100 dark:bg-red-900/30">
            <XCircle className="h-3 w-3 text-red-600 dark:text-red-400" />
          </span>
          Denied
        </div>
        <div className="flex items-center gap-1.5">
          <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300">
            <UserCheck className="h-2.5 w-2.5" />
          </span>
          Has user overrides
        </div>
      </div>
    </div>
  )
}


// ─── User Overrides Tab ────────────────────────────────────────────────────────

function UserOverridesTab() {
  const [overrides, setOverrides] = useState<PermissionOverride[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showAddForm, setShowAddForm] = useState(false)
  const [removingId, setRemovingId] = useState<string | null>(null)

  // Add form state
  const [newUserId, setNewUserId] = useState('')
  const [newFeature, setNewFeature] = useState('')
  const [newGranted, setNewGranted] = useState(true)
  const [adding, setAdding] = useState(false)

  const fetchOverrides = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const res = await adminService.getPermissionMatrix()
      const data = res.data?.matrix || res.data || {}
      setOverrides(Array.isArray(data.overrides) ? data.overrides : [])
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load overrides')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetchOverrides() }, [fetchOverrides])

  const handleAdd = async () => {
    if (!newUserId.trim() || !newFeature.trim()) {
      alert('User ID and Feature are required.')
      return
    }

    try {
      setAdding(true)
      await adminService.setUserOverride(newUserId.trim(), newFeature.trim(), newGranted)
      setNewUserId('')
      setNewFeature('')
      setNewGranted(true)
      setShowAddForm(false)
      fetchOverrides()
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to add override'
      alert(message)
    } finally {
      setAdding(false)
    }
  }

  const handleRemove = async (override: PermissionOverride) => {
    try {
      setRemovingId(override.id)
      await adminService.removeUserOverride(override.userId, override.feature)
      setOverrides((prev) => prev.filter((o) => o.id !== override.id))
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to remove override'
      alert(message)
    } finally {
      setRemovingId(null)
    }
  }

  if (loading) return <LoadingState />
  if (error) return <ErrorState message={error} onRetry={fetchOverrides} />

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          User-level overrides take precedence over role-based permissions.
        </p>
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md bg-blue-600 text-white hover:bg-blue-700 transition-colors"
        >
          <Plus className="h-4 w-4" />
          Add Override
        </button>
      </div>

      {/* Add Override Form */}
      {showAddForm && (
        <div className="rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 p-4 space-y-3">
          <h3 className="text-sm font-medium text-gray-900 dark:text-white">New User Override</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                User ID
              </label>
              <input
                type="text"
                value={newUserId}
                onChange={(e) => setNewUserId(e.target.value)}
                placeholder="Enter user ID"
                className="w-full text-sm border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Feature
              </label>
              <input
                type="text"
                value={newFeature}
                onChange={(e) => setNewFeature(e.target.value)}
                placeholder="e.g., dashboard.view"
                className="w-full text-sm border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Access
              </label>
              <select
                value={newGranted ? 'grant' : 'deny'}
                onChange={(e) => setNewGranted(e.target.value === 'grant')}
                className="w-full text-sm border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="grant">Grant</option>
                <option value="deny">Deny</option>
              </select>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleAdd}
              disabled={adding}
              className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              {adding ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
              Add
            </button>
            <button
              onClick={() => setShowAddForm(false)}
              className="text-sm px-3 py-1.5 rounded-md bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Overrides Table */}
      {overrides.length === 0 ? (
        <EmptyState message="No user-level overrides configured." />
      ) : (
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                  <th className="text-left px-4 py-3 font-medium text-gray-700 dark:text-gray-300">User ID</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-700 dark:text-gray-300">Feature</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-700 dark:text-gray-300">Access</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-700 dark:text-gray-300">Set By</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-700 dark:text-gray-300">Created</th>
                  <th className="text-right px-4 py-3 font-medium text-gray-700 dark:text-gray-300">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {overrides.map((override) => (
                  <tr key={override.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                    <td className="px-4 py-3">
                      <span className="font-mono text-xs text-gray-900 dark:text-white">
                        {override.userId.slice(0, 8)}...
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-gray-900 dark:text-white">
                        {override.feature}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${
                          override.granted
                            ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                            : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                        }`}
                      >
                        {override.granted ? (
                          <CheckCircle className="h-3 w-3" />
                        ) : (
                          <XCircle className="h-3 w-3" />
                        )}
                        {override.granted ? 'Granted' : 'Denied'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-600 dark:text-gray-400">
                      {override.setBy ? override.setBy.slice(0, 8) + '...' : '—'}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-600 dark:text-gray-400">
                      {override.createdAt ? formatDate(override.createdAt) : '—'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => handleRemove(override)}
                        disabled={removingId === override.id}
                        className="inline-flex items-center gap-1 text-xs px-2 py-1.5 rounded text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20 disabled:opacity-50 transition-colors"
                        title="Remove override"
                      >
                        {removingId === override.id ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <Trash2 className="h-3 w-3" />
                        )}
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}


// ─── Feature Flags Tab ─────────────────────────────────────────────────────────

function FeatureFlagsTab() {
  const [flags, setFlags] = useState<FeatureFlag[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [togglingId, setTogglingId] = useState<string | null>(null)
  const [showCreateForm, setShowCreateForm] = useState(false)

  // Create form state
  const [newName, setNewName] = useState('')
  const [newDescription, setNewDescription] = useState('')
  const [newEnabled, setNewEnabled] = useState(false)
  const [newTargetRoles, setNewTargetRoles] = useState('')
  const [creating, setCreating] = useState(false)

  const fetchFlags = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const res = await adminService.listFeatureFlags()
      const data = res.data?.flags || res.data || []
      setFlags(Array.isArray(data) ? data : [])
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load feature flags')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetchFlags() }, [fetchFlags])

  const handleToggle = async (flag: FeatureFlag) => {
    try {
      setTogglingId(flag.id)
      await adminService.toggleFeatureFlag(flag.id, !flag.enabled)
      setFlags((prev) =>
        prev.map((f) => (f.id === flag.id ? { ...f, enabled: !f.enabled } : f))
      )
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to toggle flag'
      alert(message)
    } finally {
      setTogglingId(null)
    }
  }

  const handleCreate = async () => {
    if (!newName.trim()) {
      alert('Flag name is required.')
      return
    }

    const targetRoles = newTargetRoles
      .split(',')
      .map((r) => r.trim())
      .filter((r) => r.length > 0)

    try {
      setCreating(true)
      await adminService.createFeatureFlag({
        name: newName.trim(),
        description: newDescription.trim() || undefined,
        enabled: newEnabled,
        targetRoles,
      })
      setNewName('')
      setNewDescription('')
      setNewEnabled(false)
      setNewTargetRoles('')
      setShowCreateForm(false)
      fetchFlags()
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to create feature flag'
      alert(message)
    } finally {
      setCreating(false)
    }
  }

  if (loading) return <LoadingState />
  if (error) return <ErrorState message={error} onRetry={fetchFlags} />

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Feature flags control access to platform features for specific user groups.
        </p>
        <button
          onClick={() => setShowCreateForm(!showCreateForm)}
          className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md bg-blue-600 text-white hover:bg-blue-700 transition-colors"
        >
          <Plus className="h-4 w-4" />
          Create Flag
        </button>
      </div>

      {/* Create Form */}
      {showCreateForm && (
        <div className="rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 p-4 space-y-3">
          <h3 className="text-sm font-medium text-gray-900 dark:text-white">New Feature Flag</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Name
              </label>
              <input
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="e.g., ai_analysis"
                className="w-full text-sm border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Target Roles (comma-separated)
              </label>
              <input
                type="text"
                value={newTargetRoles}
                onChange={(e) => setNewTargetRoles(e.target.value)}
                placeholder="e.g., admin, lender, business_owner"
                className="w-full text-sm border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Description
              </label>
              <input
                type="text"
                value={newDescription}
                onChange={(e) => setNewDescription(e.target.value)}
                placeholder="Optional description"
                className="w-full text-sm border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
              <input
                type="checkbox"
                checked={newEnabled}
                onChange={(e) => setNewEnabled(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
              />
              Enabled on creation
            </label>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleCreate}
              disabled={creating}
              className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              {creating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
              Create
            </button>
            <button
              onClick={() => setShowCreateForm(false)}
              className="text-sm px-3 py-1.5 rounded-md bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Feature Flags List */}
      {flags.length === 0 ? (
        <EmptyState message="No feature flags configured." />
      ) : (
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 divide-y divide-gray-100 dark:divide-gray-700">
          {flags.map((flag) => (
            <div
              key={flag.id}
              className="flex items-center justify-between px-4 py-4"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {flag.name}
                  </p>
                  <span
                    className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${
                      flag.enabled
                        ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                        : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                    }`}
                  >
                    {flag.enabled ? 'Active' : 'Inactive'}
                  </span>
                </div>
                {flag.description && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    {flag.description}
                  </p>
                )}
                {flag.targetRoles && flag.targetRoles.length > 0 && (
                  <div className="flex gap-1 mt-1.5 flex-wrap">
                    {flag.targetRoles.map((role) => (
                      <span
                        key={role}
                        className="inline-block text-[10px] px-1.5 py-0.5 rounded bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800"
                      >
                        {role}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <button
                onClick={() => handleToggle(flag)}
                disabled={togglingId === flag.id}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 disabled:opacity-50 ${
                  flag.enabled ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-600'
                }`}
                role="switch"
                aria-checked={flag.enabled}
                aria-label={`Toggle ${flag.name}`}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    flag.enabled ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}


// ─── Shared UI Components ──────────────────────────────────────────────────────

function LoadingState() {
  return (
    <div className="flex items-center justify-center py-12">
      <Loader2 className="h-6 w-6 animate-spin text-blue-600 dark:text-blue-400" />
      <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">Loading...</span>
    </div>
  )
}

function ErrorState({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center py-12 space-y-3">
      <AlertTriangle className="h-8 w-8 text-red-500" />
      <p className="text-sm text-red-600 dark:text-red-400">{message}</p>
      <button
        onClick={onRetry}
        className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
      >
        <RefreshCw className="h-3.5 w-3.5" />
        Retry
      </button>
    </div>
  )
}

function EmptyState({ message }: { message: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-12">
      <Shield className="h-8 w-8 text-gray-400 dark:text-gray-500" />
      <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">{message}</p>
    </div>
  )
}

// ─── Utility Functions ─────────────────────────────────────────────────────────

function formatDate(dateStr: string): string {
  try {
    const date = new Date(dateStr)
    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return dateStr
  }
}
